import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

export type HubBreadcrumbItem = {
	href: string;
	id: string;
	text: string;
};

type HubBreadcrumbsContextValue = {
	clearBreadcrumbs: () => void;
	items: readonly HubBreadcrumbItem[];
	setBreadcrumbs: (items: readonly HubBreadcrumbItem[]) => void;
};

const HubBreadcrumbsContext = createContext<HubBreadcrumbsContextValue | null>(null);

const areBreadcrumbsEqual = (
	currentItems: readonly HubBreadcrumbItem[],
	nextItems: readonly HubBreadcrumbItem[],
): boolean =>
	currentItems.length === nextItems.length &&
	currentItems.every((item, index) => {
		const nextItem = nextItems[index];
		return (
			nextItem !== undefined &&
			item.href === nextItem.href &&
			item.id === nextItem.id &&
			item.text === nextItem.text
		);
	});

export const HubBreadcrumbsProvider = ({
	children,
}: {
	children: React.ReactNode;
}): JSX.Element => {
	const [items, setItems] = useState<readonly HubBreadcrumbItem[]>([]);

	const setBreadcrumbs = useCallback((nextItems: readonly HubBreadcrumbItem[]) => {
		setItems((currentItems) =>
			areBreadcrumbsEqual(currentItems, nextItems) ? currentItems : nextItems,
		);
	}, []);
	const clearBreadcrumbs = useCallback(() => setItems([]), []);

	const value = useMemo(
		() => ({ clearBreadcrumbs, items, setBreadcrumbs }),
		[clearBreadcrumbs, items, setBreadcrumbs],
	);

	return <HubBreadcrumbsContext.Provider value={value}>{children}</HubBreadcrumbsContext.Provider>;
};

const useHubBreadcrumbsContext = (): HubBreadcrumbsContextValue => {
	const context = useContext(HubBreadcrumbsContext);
	if (!context) {
		throw new Error('Hub breadcrumb hooks must be used within HubBreadcrumbsProvider');
	}
	return context;
};

export const useRegisterHubBreadcrumbs = (items: readonly HubBreadcrumbItem[]): void => {
	const { clearBreadcrumbs, setBreadcrumbs } = useHubBreadcrumbsContext();

	useEffect(() => {
		setBreadcrumbs(items);
	}, [items, setBreadcrumbs]);

	useEffect(
		() => () => {
			clearBreadcrumbs();
		},
		[clearBreadcrumbs],
	);
};

export const useHubBreadcrumbItems = (): readonly HubBreadcrumbItem[] =>
	useHubBreadcrumbsContext().items;
